'use client';

import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Description, Field, Label } from '@headlessui/react';
import classnames from 'classnames';
import React, { forwardRef, useState } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface DateRangePickerProps {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: {
    start?: string;
    end?: string;
  };
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: string;
  showIcons?: boolean;
  separator?: string;
}

export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      value = { start: null, end: null },
      onChange,
      disabled,
      required,
      placeholder = { start: 'Start date', end: 'End date' },
      minDate,
      maxDate,
      dateFormat = 'YYYY-MM-DD',
      showIcons = true,
      separator = 'to',
      ...props
    },
    ref,
  ) => {
    const [startDate, setStartDate] = useState(value.start);
    const [endDate, setEndDate] = useState(value.end);

    // Base input classes using theme-aware variables
    const baseInputClasses = 'flex items-center rounded-theme-md border font-theme-normal transition-theme-normal focus:outline-none focus:ring-2 focus:ring-offset-2';

    // Theme-aware variant classes
    const variantInputClasses = {
      default: 'border-border bg-surface text-text placeholder-text-muted focus:border-primary focus:ring-primary',
      error: 'border-error bg-surface text-text placeholder-text-muted focus:border-error focus:ring-error',
      success: 'border-success bg-surface text-text placeholder-text-muted focus:border-success focus:ring-success',
    };

    // Theme-aware size classes
    const sizeInputClasses = {
      sm: 'px-theme-sm py-theme-xs text-theme-sm',
      md: 'px-theme-md py-theme-sm text-theme-base',
      lg: 'px-theme-lg py-theme-md text-theme-lg',
    };

    // Icon classes
    const iconClasses = useThemeClasses(classnames('flex-shrink-0 text-text-muted mr-2', size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'));

    // Separator classes
    const separatorClasses = useThemeClasses('flex-shrink-0 text-text-muted mx-3 font-theme-normal');

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    // Combine input classes
    const inputClasses = useThemeClasses(
      classnames(
        baseInputClasses,
        variantInputClasses[actualVariant],
        sizeInputClasses[size],
        showIcons ? 'pl-theme-sm pr-theme-sm' : 'px-theme-md',
        fullWidth && 'flex-1',
        disabled && 'opacity-50 cursor-not-allowed',
      ),
    );

    // Container classes
    const containerClasses = useThemeClasses(classnames('relative flex items-center', fullWidth && 'w-full'));

    // Label classes
    const labelClasses = useThemeClasses(classnames('block font-theme-medium text-theme-sm text-text mb-theme-xs', disabled && 'opacity-50'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-theme-xs text-theme-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const date = e.target.value ? new Date(e.target.value) : null;
      setStartDate(date);
      onChange?.({ start: date, end: endDate });
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const date = e.target.value ? new Date(e.target.value) : null;
      setEndDate(date);
      onChange?.({ start: startDate, end: date });
    };

    const formatDate = (date: Date | null): string => {
      if (!date) return '';
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };

    return (
      <div ref={ref} className={containerClasses}>
        <Field {...props}>
          {label && (
            <Label className={labelClasses}>
              {label}
              {required && <span className="text-error ml-1">*</span>}
            </Label>
          )}

          <div className="flex items-center w-full">
            {/* Start Date Input */}
            <div className="relative flex-1">
              {showIcons && <FontAwesomeIcon icon={faCalendar} className={iconClasses} />}
              <input
                type="date"
                className={inputClasses}
                value={formatDate(startDate)}
                onChange={handleStartDateChange}
                disabled={disabled}
                min={minDate ? formatDate(minDate) : undefined}
                max={maxDate ? formatDate(maxDate) : undefined}
                placeholder={placeholder.start}
              />
            </div>

            {/* Separator */}
            <span className={separatorClasses}>{separator}</span>

            {/* End Date Input */}
            <div className="relative flex-1">
              {showIcons && <FontAwesomeIcon icon={faCalendar} className={iconClasses} />}
              <input
                type="date"
                className={inputClasses}
                value={formatDate(endDate)}
                onChange={handleEndDateChange}
                disabled={disabled}
                min={startDate ? formatDate(startDate) : minDate ? formatDate(minDate) : undefined}
                max={maxDate ? formatDate(maxDate) : undefined}
                placeholder={placeholder.end}
              />
            </div>
          </div>

          {(helperText || error || success) && <Description className={helperTextClasses}>{error || success || helperText}</Description>}
        </Field>
      </div>
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
